import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Bell, BellOff, Check } from 'lucide-react';
import { Button } from '@attendance/ui/components/Button';
import { IconButton } from '@attendance/ui/components/IconButton';
import { useDismiss } from '@attendance/ui/dismiss';
import { toast } from 'sonner';
import { notificationsApi, type Notification } from '../../api/notifications.api.js';
import { UNREAD_COUNT_QUERY_KEY } from '../../hooks/useUnreadNotifications.js';
import { typeStyle } from '../../routes/notifications/notificationType.js';
import { useFormatDate, useFormatTime } from '../../hooks/useFormatDate.js';
import { friendlyMessage } from '../../lib/errors.js';
import { ymdLocal } from '../../lib/dates.js';
import { Skeleton } from '../common/Skeleton.js';
import { UnreadBadge } from './UnreadBadge.js';
import { resolveNotificationLink } from '../../routes/notifications/notificationLink.js';
import { useAuthStore } from '../../stores/authStore.js';
import { notificationText } from '../../lib/notificationText.js';
import { ICON } from '@attendance/ui';

/** How many rows the panel shows before deferring to the full page. */
const PREVIEW_COUNT = 5;

/**
 * The app-bar bell, and the panel it opens.
 *
 * The bell used to be a bare link straight to /notifications: the only way to
 * find out whether a notification mattered was to LEAVE the page you were on.
 * That is a bad trade for the common case, which is "something arrived, is it
 * urgent?" — a question answerable in a glance if the answer is allowed to
 * appear in place.
 *
 * The panel is deliberately a PREVIEW, not a second inbox. Five rows, then a
 * door to the real page. Everything that needs filtering, grouping by day or
 * bulk work stays on /notifications, so the two never become rival
 * implementations of the same screen — they already share the type-colour map
 * and the unread-count query key for exactly that reason.
 */
export function NotificationBell({ enabled }: { enabled: boolean }): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: count = 0 } = useQuery({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
    enabled,
  });

  // Only fetched once the panel is opened — the bell's badge runs off the
  // count endpoint, so an unopened panel costs nothing.
  const {
    data: items,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(false),
    enabled: enabled && open,
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
    void qc.invalidateQueries({ queryKey: UNREAD_COUNT_QUERY_KEY });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    // No success toast: this rides on opening the row, which navigates away —
    // the visible outcome is the destination and the unread count dropping.
    onSuccess: invalidate,
    onError: (err) => toast.error(friendlyMessage(err)),
  });
  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: (marked) => {
      invalidate();
      // The SERVER's count (§3.12): a stale badge pressed after another tab
      // cleared the inbox marked nothing, and must not say "all".
      if (marked > 0) toast.success(t('notifications.toast.markedRead', 'Marked {{count}} as read', { count: marked }));
      else toast.info(t('notifications.toast.noneUnread', 'Nothing was unread'));
    },
    onError: (err) => toast.error(friendlyMessage(err)),
  });

  // Close on an outside press and on Escape. Escape returns focus to the bell —
  // without that, dismissing the panel by keyboard drops the caret at the top
  // of the document and the next Tab restarts the whole header.
  useDismiss(open, [rootRef], (how) => {
    setOpen(false);
    if (how === 'escape') buttonRef.current?.focus();
  });

  const shown = (items ?? []).slice(0, PREVIEW_COUNT);

  function openRow(n: Notification): void {
    if (!n.isRead) markRead.mutate(n.id);
    setOpen(false);
    // An admin goes to the area's admin door, not into "My workspace".
    navigate(resolveNotificationLink(n.link, user) ?? '/notifications');
  }

  return (
    <div ref={rootRef} className="relative">
      <IconButton
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        label={t('appShell.notifications', 'Notifications')}
        // `relative` so the unread count can anchor to the button; the
        // primitive is already `state-layer`, which sets `isolation`, so the
        // badge stays above the hover overlay.
        className="relative"
      >
        <Bell size={ICON.feature} />
        <UnreadBadge count={count} className="absolute end-1 top-1" />
      </IconButton>

      {open && (
        <div
          role="dialog"
          aria-label={t('notifications.title', 'Notifications')}
          // `z-50` (below) only ranks against this panel's OWN stacking
          // context — a positioned ancestor with its own z-index opens one,
          // and this panel's is `<header>` in AppShell.tsx. It used to lose to
          // any page's sticky toolbar (also positioned, tied at the same
          // z-index one level up) the moment the panel scrolled low enough to
          // overlap it; see the note on `<header>`'s own z-index there for the
          // fix. `z-50` is still the right number for this panel's OWN tier
          // (level with Dialog/Sidebar-drawer/Combobox) — nothing here needed
          // to change, only the header hosting it.
          //
          // ANCHORED above `sm`, INSET below it.
          //
          // This was `absolute end-0` at every width, with the width capped as
          // `min(22rem, 100vw - 1.5rem)`. That cap stops the panel making the
          // PAGE scroll, which is what its old comment claimed — but the panel
          // is anchored to a button sitting ~60px from the right edge, so a
          // 351px panel simply hung off the other side. Measured at 375px:
          // `left: -36px`, i.e. 36px of it was off-screen and unreachable.
          //
          // Below `sm` it is therefore `fixed` and inset from both edges, which
          // is the same 16px gutter the Dialog and the command palette already
          // use — so all three overlays line up on a phone instead of each
          // choosing its own answer. From `sm` up there is room to anchor, and
          // it goes back to hanging off the trigger.
          //
          // `start-4`/`end-4` rather than `left`/`right`: the panel has to hang
          // off the correct edge in Arabic too.
          //
          // The desktop half is an EXPLICIT width, not `w-full` + `max-w`.
          // `w-full` resolves against the nearest positioned ancestor, which
          // here is the `div.relative` wrapping only the bell button — about
          // 40px. A max-width caps, it never expands, so `sm:w-full
          // sm:max-w-popover` collapsed the panel to the width of the button
          // and `overflow-hidden` clipped its contents. That was a regression I
          // introduced when replacing the original `w-[min(22rem,…)]`: I
          // verified the phone case and never re-measured desktop.
          //
          // `shadow-popover`, not the deprecated `shadow-elev-lg` alias. This
          // panel IS a popover, and the alias resolved to `--shadow-hover` —
          // the lift weight, which says "the pointer is on it" and is a full
          // step lighter than the role. That mattered here more than most:
          // unlike the command palette this panel has no scrim behind it, so
          // the shadow is the only thing separating it from whatever it covers,
          // and on a 1.043:1 page/card step the hairline alone does not carry
          // it. This one is a real (intended) visual change, not a rename.
          className="fixed start-4 end-4 top-[calc(theme(spacing.header)+0.5rem)] z-50 overflow-hidden rounded-panel border border-border bg-surface shadow-popover sm:absolute sm:inset-x-auto sm:end-0 sm:top-auto sm:mt-2 sm:w-[var(--popover-max)]"
        >
          <div className="flex items-center justify-between gap-element border-b border-border px-cell-x py-cell-y">
            <p className="flex items-center gap-element text-h3 text-foreground">
              <Bell size={ICON.control} className="text-primary" aria-hidden />
              {t('notifications.title', 'Notifications')}
            </p>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="inline-flex items-center gap-tight rounded-control px-1.5 py-1 text-copy-sm font-medium text-primary hover:underline disabled:opacity-60"
              >
                <Check size={ICON.inline} aria-hidden />
                {markAll.isPending
                  ? t('notifications.markingAll', 'Marking…')
                  : t('notifications.markAllRead', 'Mark all read')}
              </button>
            )}
          </div>

          <div className="max-h-[min(24rem,60vh)] overflow-y-auto">
            {isError ? (
              // A failure must never render as "nothing to see" — that is how a
              // broken feed reads as an empty one.
              <div className="space-y-element p-4">
                <p className="text-copy font-medium text-danger">
                  {t('notifications.error.title', "Couldn't load your notifications.")}
                </p>
                <p className="text-copy text-danger">{friendlyMessage(error)}</p>
                <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
                  {t('notifications.error.tryAgain', 'Try again')}
                </Button>
              </div>
            ) : isLoading ? (
              <div className="space-y-element p-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : shown.length === 0 ? (
              <div className="flex flex-col items-center gap-element px-4 py-10 text-center">
                <BellOff size={ICON.hero} className="text-muted-foreground" aria-hidden />
                <p className="text-copy text-muted-foreground">
                  {t('notifications.empty', "You're all caught up.")}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {shown.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openRow(n)}
                      className={`flex w-full items-start gap-2.5 px-cell-x py-cell-y text-start hover:bg-muted ${
                        n.isRead ? '' : 'bg-primary/5'
                      }`}
                    >
                      <span
                        className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${typeStyle(
                          n.type
                        )}`}
                      >
                        <Bell size={ICON.inline} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-control">
                          {!n.isRead && (
                            <span
                              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                              aria-hidden
                            />
                          )}
                          <span
                            className={`truncate text-copy text-foreground ${
                              n.isRead ? 'font-medium' : 'font-semibold'
                            }`}
                          >
                            {notificationText(t, n).title}
                          </span>
                        </span>
                        {/* One line only. The panel is a preview; the full
                            message is on the page this row opens. */}
                        <span className="mt-0.5 block truncate text-copy-sm text-muted-foreground">
                          {notificationText(t, n).message}
                        </span>
                        <span className="mt-1 block text-copy-sm tabular-nums text-muted-foreground">
                          <Stamp iso={n.createdAt} />
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-tight border-t border-border px-cell-x py-cell-y text-center text-label text-primary hover:bg-muted"
          >
            {t('notifications.viewAll', 'View all notifications')}
            {/*
              A drawn glyph, not a literal "→": the character is a fixed shape,
              so it kept pointing left-to-right under Arabic while the text ran
              the other way. The icon flips with `dir` and stays decorative —
              the accessible name is the link text.
            */}
            <ArrowRight size={ICON.inline} className="rtl:-scale-x-100" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Clock time for today, date + time before that. A list where every row reads
 * "14 Aug" buries the one part that actually differs between this morning's
 * notification and last week's.
 */
function Stamp({ iso }: { iso: string }): JSX.Element {
  const formatDate = useFormatDate();
  const formatTime = useFormatTime();
  const d = new Date(iso);
  const isToday = !Number.isNaN(d.getTime()) && ymdLocal(d) === ymdLocal();
  return <>{isToday ? formatTime(iso) : `${formatDate(iso)}, ${formatTime(iso)}`}</>;
}
